// @ts-nocheck
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Trophy, Users, Calendar } from "lucide-react";
import { BracketGenerator, type Team, type BracketStructure, type Match } from "@/utils/bracketGenerator";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { TournamentService } from "@/modules/tournaments/services/TournamentService";

interface Tournament {
  id: string;
  name: string;
  tournament_type: string;
  max_teams: number;
  seeding_type: string;
}

interface BracketGenerationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  tournament: Tournament;
  teams: Array<{ id: string; team_name: string; seed_number: number }>;
}

export const BracketGenerationDialog: React.FC<BracketGenerationDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  tournament,
  teams,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewBracket, setPreviewBracket] = useState<BracketStructure | null>(null);
  const [step, setStep] = useState<'preview' | 'confirm'>('preview');

  const generatePreview = () => {
    try {
      const teamsForGeneration: Team[] = teams.map(team => ({
        id: team.id,
        name: team.team_name,
        seed_number: team.seed_number
      }));

      const bracket = BracketGenerator.generateBracket(
        tournament.tournament_type,
        tournament.id,
        teamsForGeneration
      );

      setPreviewBracket(bracket);
    } catch (error: any) {
      console.error("Error generating bracket preview:", error);
      toast.error("Failed to generate bracket preview");
    }
  };

  React.useEffect(() => {
    if (isOpen && teams.length > 0) {
      generatePreview();
    }
  }, [isOpen, teams, tournament]);

  const handleGenerate = async () => {
    if (!previewBracket) return;

    setIsGenerating(true);
    try {
      // Insert all matches into the database
      const { data: insertedMatches, error } = await supabase
        .from("tournament_matches")
        .insert(previewBracket.matches)
        .select();

      if (error) throw error;

      console.log('[BRACKET_GEN] Inserted matches:', insertedMatches?.length);

      // Link matches for auto-advancement (single/double elimination)
      if (insertedMatches && insertedMatches.length > 0) {
        const links = BracketGenerator.linkBracketMatches(
          insertedMatches as any[],
          tournament.tournament_type
        );

        console.log('[BRACKET_GEN] Generated links:', links.length);

        // Update each match with its next_match_id links
        for (const link of links) {
          const updateData: any = {
            next_match_id: link.nextMatchId,
            next_match_slot: link.nextMatchSlot
          };

          if (link.loserNextMatchId) {
            updateData.loser_next_match_id = link.loserNextMatchId;
            updateData.loser_next_match_slot = link.loserNextMatchSlot;
          }

          const { error: linkError } = await supabase
            .from("tournament_matches")
            .update(updateData)
            .eq("id", link.matchId);

          if (linkError) {
            console.error('[BRACKET_GEN] Error linking match:', link.matchId, linkError);
          }
        }

        console.log('[BRACKET_GEN] All matches linked for auto-advancement');
      }

      // Create pool records for pool_play_playoffs tournaments
      if (tournament.tournament_type === 'pool_play_playoffs' && insertedMatches) {
        try {
          const tournamentService = new TournamentService(supabase, tournament.id);

          // First delete any existing pools (in case of regeneration)
          await tournamentService.deletePools(tournament.id);

          // Derive pool info from the generated matches
          const poolMap = new Map<string, { name: string; pool_order: number; teamIds: Set<string> }>();

          for (const match of previewBracket.matches) {
            if (match.bracket_type === 'pool' && match.pool_id) {
              if (!poolMap.has(match.pool_id)) {
                const poolLetter = match.pool_id.replace('pool_', '').toUpperCase();
                poolMap.set(match.pool_id, {
                  name: `Pool ${poolLetter}`,
                  pool_order: poolMap.size + 1,
                  teamIds: new Set()
                });
              }
              const pool = poolMap.get(match.pool_id)!;
              if (match.team1_id) pool.teamIds.add(match.team1_id);
              if (match.team2_id) pool.teamIds.add(match.team2_id);
            }
          }

          // Create pool records
          const poolsToCreate = Array.from(poolMap.entries()).map(([_, pool]) => ({
            name: pool.name,
            pool_order: pool.pool_order
          }));

          const createdPools = await tournamentService.createPools(tournament.id, poolsToCreate);

          // Create team-pool assignments
          const assignments: { pool_id: string; team_id: string }[] = [];
          const poolEntries = Array.from(poolMap.entries());

          for (let i = 0; i < poolEntries.length; i++) {
            const [_, poolInfo] = poolEntries[i];
            const createdPool = createdPools[i];
            for (const teamId of poolInfo.teamIds) {
              assignments.push({ pool_id: createdPool.id, team_id: teamId });
            }
          }

          await tournamentService.assignTeamsToPools(assignments);
          console.log('[BRACKET_GEN] Pool records created:', createdPools.length, 'pools,', assignments.length, 'team assignments');
        } catch (poolError) {
          console.error('[BRACKET_GEN] Error creating pool records:', poolError);
          // Don't fail the whole generation — pool records are supplementary
        }
      }

      // Update tournament status to 'active'
      const { error: tournamentError } = await supabase
        .from("tournaments")
        .update({ status: 'active' })
        .eq("id", tournament.id);

      if (tournamentError) throw tournamentError;

      toast.success("Tournament bracket generated with auto-advancement!");
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error generating bracket:", error);
      toast.error("Failed to generate tournament bracket");
    } finally {
      setIsGenerating(false);
    }
  };

  const getTournamentTypeDisplay = (type: string) => {
    switch (type) {
      case "single_elimination":
        return "Single Elimination";
      case "double_elimination":
        return "Double Elimination";
      case "round_robin":
        return "Round Robin";
      case "pool_play_playoffs":
        return "Pool Play + Playoffs";
      default:
        return type;
    }
  };

  const getBracketTypeDisplay = (type: string) => {
    switch (type) {
      case "winners":
        return "Winners Bracket";
      case "losers":
        return "Losers Bracket";
      case "pool":
        return "Pool Play";
      case "final":
        return "Finals";
      default:
        return type;
    }
  };

  const groupMatchesByBracketType = (matches: Match[]) => {
    const grouped = matches.reduce((acc, match) => {
      if (!acc[match.bracket_type]) {
        acc[match.bracket_type] = [];
      }
      acc[match.bracket_type].push(match);
      return acc;
    }, {} as Record<string, Match[]>);

    return grouped;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Generate Tournament Bracket
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Tournament Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{tournament.name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium text-muted-foreground">Type:</span>
                  <div>{getTournamentTypeDisplay(tournament.tournament_type)}</div>
                </div>
                <div>
                  <span className="font-medium text-muted-foreground">Teams:</span>
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {teams.length}/{tournament.max_teams}
                  </div>
                </div>
                <div>
                  <span className="font-medium text-muted-foreground">Seeding:</span>
                  <div>{tournament.seeding_type}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bracket Preview */}
          {previewBracket && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Bracket Preview</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-muted-foreground">Total Rounds:</span>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {previewBracket.rounds}
                    </div>
                  </div>
                  <div>
                    <span className="font-medium text-muted-foreground">Total Matches:</span>
                    <div>{previewBracket.totalMatches}</div>
                  </div>
                  <div>
                    <span className="font-medium text-muted-foreground">Status:</span>
                    <Badge variant="outline">Ready to Generate</Badge>
                  </div>
                </div>

                {/* Match Breakdown by Bracket Type */}
                <div className="space-y-4">
                  <h4 className="font-medium">Match Breakdown:</h4>
                  {Object.entries(groupMatchesByBracketType(previewBracket.matches)).map(([bracketType, matches]) => (
                    <div key={bracketType} className="border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-medium">{getBracketTypeDisplay(bracketType)}</h5>
                        <Badge variant="secondary">{matches.length} matches</Badge>
                      </div>
                      
                      {bracketType === 'pool' && (
                        <div className="text-sm text-muted-foreground">
                          All teams will play each other within their pools
                        </div>
                      )}
                      
                      {(bracketType === 'winners' || bracketType === 'losers') && (
                        <div className="text-sm text-muted-foreground">
                          {bracketType === 'winners' ? 'Main tournament bracket' : 'Second chance bracket'}
                        </div>
                      )}
                      
                      {bracketType === 'final' && (
                        <div className="text-sm text-muted-foreground">
                          Championship match
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Team Seeding Preview */}
                <div className="space-y-2">
                  <h4 className="font-medium">Team Seeding:</h4>
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                    {teams
                      .sort((a, b) => a.seed_number - b.seed_number)
                      .map((team) => (
                        <div key={team.id} className="flex items-center justify-between text-sm border rounded p-2">
                          <span>#{team.seed_number} {team.team_name}</span>
                        </div>
                      ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={isGenerating}>
              Cancel
            </Button>
            <Button 
              onClick={handleGenerate} 
              disabled={isGenerating || !previewBracket}
              className="flex items-center gap-2"
            >
              {isGenerating && <Loader2 className="h-4 w-4 animate-spin" />}
              Generate Bracket
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};